<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: 'My Organizations',
          route: '/organizations',
        },
        {
          title: organization.name,
          route: `/organization/${organizationId}`,
        },
        {
          title: title,
          route: `/organization-team/${teamId || 'create'}?organizationId=${organizationId}`,
        },
      ]"
    />
    <MainContentPanel :title="title">
      <form class="uk-flex uk-flex-column">
        <form-group
          v-if="
            !formFields.name.uiModeHidden ||
            (formFields.name.uiModeHidden && uiMode !== formFields.name.uiModeHidden)
          "
          class="uk-margin uk-width-1-1"
          :class="{ error: v$.form.name.$error }"
          :validator="v$.form.name"
        >
          <span class="uk-inline full-width">
            <span
              v-if="formFields.name.icon"
              class="uk-form-icon"
              :uk-icon="`icon: ${formFields.name.icon}`"
            ></span>
            <input
              class="uk-input"
              :type="formFields.name.type"
              :placeholder="formFields.name.placeholder"
              v-model="form.name"
              required
              @input="v$.form.name.$touch()"
            />
          </span>
        </form-group>

        <div class="uk-flex uk-flex-row" style="min-height: 400px">
          <div style="width: 45%">
            <HeadingSecondary title="Organization Members" :small="true" />
            <DataTable
              :value="availableOrganizationMembers"
              :row-hover="true"
              :selection.sync="selectedOrgMembers"
              selectionMode="multiple"
              dataKey="_id"
            >
              <Column header="Name" :sortable="true">
                <template #body="memberProps">
                  <span>{{
                    memberProps.data.user
                      ? memberProps.data.user.name
                      : memberProps.data.invitation.name
                  }}</span>
                </template>
              </Column>
            </DataTable>
          </div>

          <div class="uk-flex uk-flex-column uk-margin-small-left uk-margin-small-right">
            <div style="flex-grow: 1"></div>
            <div>
              <div class="uk-button uk-button-secondary uk-border-rounded-top" @click="moveToTeam">
                <span uk-icon="icon: arrow-right"></span>
              </div>
            </div>
            <div>
              <div
                class="uk-button uk-button-secondary uk-border-rounded-bottom"
                @click="moveFromTeam"
              >
                <span uk-icon="icon: arrow-left"></span>
              </div>
            </div>
            <div style="flex-grow: 1"></div>
          </div>

          <div style="flex-grow: 1">
            <HeadingSecondary title="Team Members" :small="true" />
            <DataTable
              :value="teamMembers"
              :row-hover="true"
              :selection.sync="selectedTeamMembers"
              selectionMode="multiple"
              dataKey="organizationMember._id"
            >
              <Column header="Name" :sortable="true">
                <template #body="memberProps">
                  <span>{{
                    memberProps.data.organizationMember.user
                      ? memberProps.data.organizationMember.user.name
                      : memberProps.data.organizationMember.invitation.name
                  }}</span>
                </template>
              </Column>
              <Column header="Role" :sortable="true">
                <template #body="memberProps">
                  <select
                    class="uk-select"
                    :id="`${memberProps.data._id}-role`"
                    :name="`${memberProps.data._id}-role`"
                    v-model="memberProps.data.role"
                  >
                    <option :value="option" v-for="option in roleOptions" :key="option">
                      {{ option }}
                    </option>
                  </select>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>

        <div class="uk-margin uk-width-1-1">
          <button
            class="uk-button uk-button-default uk-border-rounded uk-button-primary uk-width-1-1"
            @click.prevent="submitForm"
            @keypress.enter="submitForm"
            :disabled="v$.$invalid"
          >
            Save
            <InlineLoader class="uk-margin-left" :status="savingStatus" />
          </button>
        </div>
      </form>
    </MainContentPanel>
  </div>
</template>

<script>
import { reactive, computed } from 'vue';
import InlineLoader from '@/components/general/InlineLoader';
import MainContentPanel from '@/components/display/general/MainContentPanel';
import HeadingSecondary from '@/components/general/HeadingSecondary';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import BreadCrumb from '@/components/general/BreadCrumb';
import { mapActions, mapMutations, mapState } from 'vuex';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { xhr } from '@/xhr/endpoints';

export default {
  name: 'OrganizationTeamDetails',
  components: {
    InlineLoader,
    MainContentPanel,
    HeadingSecondary,
    DataTable,
    Column,
    BreadCrumb,
  },
  setup() {
    const form = reactive({
      name: '',
    });

    const rules = computed(() => ({
      form: {
        name: { required },
      },
    }));

    const v$ = useVuelidate(rules, { form });

    return { form, v$: v$.value };
  },

  async mounted() {
    this.organizationId = this.$route.query.organizationId;
    this.teamId = this.$route.params.id;
    //
    // reload organization if no data exists
    if (!this.organization) await this.loadOrganizationDetails(this.$route.query.organizationId);
    //
    // lodad organization members
    const membersData = await this.loadOrganizationMembers({
      page: 0,
      resultsPerPage: 100,
      sortColumn: '',
      sortDirection: '',
      filters: '',
    });
    this.organizationMembers = membersData ? membersData.results : [];
    //
    // load team data
    if (this.teamId) {
      const teamDetails = await this.getOrganizationTeamDetails(this.teamId);
      console.log('teamDetails', teamDetails);
      const team = teamDetails;
      this.form.name = team.name;
      //
      // merge team member details
      this.teamMembers = team.members.map(teamMember => {
        return {
          ...teamMember,
          organizationMember: {
            ...this.organizationMembers.find(
              orgMember => orgMember._id === teamMember.organizationMember._id
            ),
          },
        };
      });
    }
  },

  data() {
    return {
      roleOptions: ['Member', 'Team Lead'],
      teams: [],
      selectedOrgMembers: [],
      organizationMembers: [],
      selectedTeamMembers: [],
      teamMembers: [],
      teamId: null,
      organizationId: null,
      formFields: {
        name: {
          type: 'text',
          icon: null,
          placeholder: 'Give your team a name',
        },
      },
      savingStatus: '',
      pageSize: 10,
    };
  },

  computed: {
    ...mapState(['idToken', 'organizationDetailsCache']),

    title() {
      return `${this.teamId ? 'Edit' : 'Create'} Organization Team`;
    },
    organization() {
      return this.organizationDetailsCache[this.organizationId];
    },

    availableOrganizationMembers() {
      const ids = this.teamMembers.map(x => x.organizationMember._id);
      return this.organizationMembers.filter(x => !ids.includes(x._id));
    },

    membersTableConfig() {
      return [
        {
          column: 'Name',
          field: 'name',
          model: row => (row.user ? row.user.name : row.invitation.name),
          sortable: true,
        },
      ];
    },

    membersTableOptions() {
      return {
        globalFilterFields: ['name'],
        selection: this.selectedOrgMembers,
        selectionMode: 'multiple',
      };
    },
  },

  methods: {
    ...mapMutations(['setUserData', 'cacheOrganizationDetails']),
    ...mapActions([
      'loadOrganizationDetails',
      'loadOrganizationMembers',
      'getOrganizationTeamDetails',
    ]),
    async submitForm() {
      this.savingStatus = 'Loading';

      if (this.teamId) {
        try {
          const updatedTeam = {
            _id: this.teamId,
            name: this.form.name,
            members: this.teamMembers.map(teamMember => ({
              memberId: teamMember.organizationMember._id,
              role: teamMember.role,
            })),
          };

          const updatedOrgDetails = await xhr.updateOrganizationTeam(
            this.organizationId,
            updatedTeam,
            this.idToken
          );
          this.cacheOrganizationDetails(updatedOrgDetails);
          this.savingStatus = 'Done';
          this.$router.push(`/organization/${this.organizationId}`);
        } catch {
          this.savingStatus = 'Failed';
        }
      } else {
        try {
          const newTeam = {
            name: this.form.name,
            members: this.teamMembers.map(teamMember => ({
              memberId: teamMember.organizationMember._id,
              role: teamMember.role,
            })),
          };
          const updatedOrgDetails = await xhr.addOrganizationTeam(
            this.organizationId,
            newTeam,
            this.idToken
          );
          this.cacheOrganizationDetails(updatedOrgDetails);
          this.savingStatus = 'Done';
          this.$router.push(`/organization/${this.organizationId}`);
        } catch {
          this.savingStatus = 'Failed';
        }
      }
    },
    moveToTeam() {
      this.teamMembers = [
        ...this.teamMembers,
        ...this.selectedOrgMembers.map(x => ({
          organizationMember: x,
          role: 'Member',
        })),
      ];
      this.selectedOrgMembers = [];
    },
    moveFromTeam() {
      const ids = this.selectedTeamMembers.map(x => x.organizationMember._id);
      this.teamMembers = this.teamMembers.filter(
        teamMember => !ids.includes(teamMember.organizationMember._id)
      );
      this.selectedTeamMembers = [];
    },
  },
};
</script>

<style scoped></style>
